/**
 * Customer Purchase Order Service
 *
 * API client for managing customer purchase orders (POs received from customers).
 */

import {
  get,
  post,
  put,
  buildQueryString,
  type ApiResponse,
} from './api-client';
import { getCurrencyConfig, fromSmallestUnit, toSmallestUnit } from '../utils/formatters';

import type {
  CustomerPurchaseOrder,
  CustomerPOListResponse,
  CustomerPOListFilters,
  CreateCustomerPORequest,
  CancelCustomerPORequest,
} from '../types/customer-purchase-order';


// ============================================================================
// API Functions
// ============================================================================

/**
 * List customer purchase orders for a ledger.
 *
 * @param orgId - Organization ID
 * @param ledgerId - Ledger ID
 * @param filters - Optional filters (customerId, status, dates, hasBalance)
 * @param pageSize - Page size (default: 50)
 * @param pageToken - Pagination token
 * @returns List of customer POs with pagination
 */
export async function listCustomerPurchaseOrders(
  orgId: string,
  ledgerId: string,
  filters?: CustomerPOListFilters,
  pagination?: { pageSize?: number; pageToken?: string }
): Promise<ApiResponse<CustomerPOListResponse>> {
  const { pageSize, pageToken } = pagination ?? {}
  const query = buildQueryString({
    pageSize,
    pageToken,
    customerId: filters?.customerId,
    status: filters?.status,
    fromDate: filters?.fromDate,
    toDate: filters?.toDate,
    hasBalance: filters?.hasBalance,
  });

  return get<CustomerPOListResponse>(
    `/organizations/${orgId}/ledgers/${ledgerId}/customer-purchase-orders${query}`
  );
}

/**
 * Get a customer purchase order by ID.
 *
 * @param orgId - Organization ID
 * @param cpoId - Customer PO ID
 * @returns Customer PO details with items
 */
export async function getCustomerPurchaseOrder(
  orgId: string,
  cpoId: string
): Promise<ApiResponse<CustomerPurchaseOrder>> {
  return get<CustomerPurchaseOrder>(
    `/organizations/${orgId}/customer-purchase-orders/${cpoId}`
  );
}

/**
 * Create a new customer purchase order.
 *
 * @param orgId - Organization ID
 * @param ledgerId - Ledger ID
 * @param data - Customer PO creation request
 * @returns Created customer PO
 */
export async function createCustomerPurchaseOrder(
  orgId: string,
  ledgerId: string,
  data: CreateCustomerPORequest
): Promise<ApiResponse<CustomerPurchaseOrder>> {
  return post<CustomerPurchaseOrder>(
    `/organizations/${orgId}/ledgers/${ledgerId}/customer-purchase-orders`,
    data
  );
}

/**
 * Update an existing customer purchase order.
 *
 * Only customer POs in 'received' status can be updated.
 *
 * @param orgId - Organization ID
 * @param cpoId - Customer PO ID
 * @param data - Customer PO update request
 * @returns Updated customer PO
 */
export async function updateCustomerPurchaseOrder(
  orgId: string,
  cpoId: string,
  data: CreateCustomerPORequest
): Promise<ApiResponse<CustomerPurchaseOrder>> {
  return put<CustomerPurchaseOrder>(
    `/organizations/${orgId}/customer-purchase-orders/${cpoId}`,
    data
  );
}

/**
 * Activate a customer purchase order.
 *
 * Transitions status from 'received' to 'active', making it available for invoicing.
 *
 * @param orgId - Organization ID
 * @param cpoId - Customer PO ID
 * @returns Updated customer PO
 */
export async function activateCustomerPurchaseOrder(
  orgId: string,
  cpoId: string
): Promise<ApiResponse<CustomerPurchaseOrder>> {
  return post<CustomerPurchaseOrder>(
    `/organizations/${orgId}/customer-purchase-orders/${cpoId}/activate`,
    {}
  );
}

/**
 * Complete a customer purchase order.
 *
 * Marks the customer PO as fully fulfilled.
 *
 * @param orgId - Organization ID
 * @param cpoId - Customer PO ID
 * @returns Updated customer PO
 */
export async function completeCustomerPurchaseOrder(
  orgId: string,
  cpoId: string
): Promise<ApiResponse<CustomerPurchaseOrder>> {
  return post<CustomerPurchaseOrder>(
    `/organizations/${orgId}/customer-purchase-orders/${cpoId}/complete`,
    {}
  );
}

/**
 * Cancel a customer purchase order.
 *
 * @param orgId - Organization ID
 * @param cpoId - Customer PO ID
 * @param reason - Cancellation reason (required)
 * @returns Updated customer PO
 */
export async function cancelCustomerPurchaseOrder(
  orgId: string,
  cpoId: string,
  reason: string
): Promise<ApiResponse<CustomerPurchaseOrder>> {
  const requestData: CancelCustomerPORequest = { reason };

  return post<CustomerPurchaseOrder>(
    `/organizations/${orgId}/customer-purchase-orders/${cpoId}/cancel`,
    requestData
  );
}

/**
 * Mark a customer purchase order as expired.
 *
 * @param orgId - Organization ID
 * @param cpoId - Customer PO ID
 * @returns Updated customer PO
 */
export async function markCustomerPurchaseOrderExpired(
  orgId: string,
  cpoId: string
): Promise<ApiResponse<CustomerPurchaseOrder>> {
  return post<CustomerPurchaseOrder>(
    `/organizations/${orgId}/customer-purchase-orders/${cpoId}/mark-expired`,
    {}
  );
}

/**
 * Search customer POs for invoicing.
 *
 * Returns active customer POs with available balance for a specific customer.
 * Used when creating invoices to link them to customer POs.
 *
 * @param orgId - Organization ID
 * @param ledgerId - Ledger ID
 * @param customerId - Customer ID to filter by
 * @returns Customer POs with available balance
 */
export async function searchCustomerPOsForInvoicing(
  orgId: string,
  ledgerId: string,
  customerId?: string
): Promise<ApiResponse<CustomerPOListResponse>> {
  const query = buildQueryString({
    customerId,
  });

  return get<CustomerPOListResponse>(
    `/organizations/${orgId}/ledgers/${ledgerId}/customer-purchase-orders/search-for-invoicing${query}`
  );
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Calculate total amount for a customer PO from its items.
 *
 * @param items - Customer PO items
 * @returns Total amount in smallest currency unit (paisa)
 */
export function calculateCustomerPOTotal(
  items: Array<{ quantity: number; unitPrice: number }>
): number {
  return items.reduce((total, item) => {
    return total + Math.round(item.quantity * item.unitPrice);
  }, 0);
}

/**
 * Format amount from smallest currency unit to decimal string.
 *
 * @param amountInPaisa - Amount in paisa (or cents)
 * @param currency - Currency code (default: 'INR')
 * @returns Formatted amount string (e.g., "1,234.56")
 */
export function formatCustomerPOAmount(
  amountInSmallestUnit: number,
  currency: string = 'INR'
): string {
  const config = getCurrencyConfig(currency);
  const decimal = fromSmallestUnit(amountInSmallestUnit, currency);

  return new Intl.NumberFormat(config.locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: config.decimalPlaces,
    maximumFractionDigits: config.decimalPlaces,
  }).format(decimal);
}

/**
 * Parse amount from decimal string to smallest currency unit.
 *
 * @param amountString - Amount as decimal string (e.g., "1234.56")
 * @param currency - Currency code for correct decimal handling
 * @returns Amount in smallest currency unit
 */
export function parseCustomerPOAmount(amountString: string, currency: string = 'INR'): number {
  const amount = parseFloat(amountString);
  if (isNaN(amount)) {
    return 0;
  }
  return toSmallestUnit(amount, currency);
}

/**
 * Check if a customer PO can be linked to an invoice.
 *
 * @param po - Customer PO
 * @returns True if PO can be linked to invoice
 */
export function canLinkToInvoice(po: CustomerPurchaseOrder): boolean {
  return po.status === 'active' && po.balance > 0;
}
